import isEqual from 'lodash.isequal';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import type Address from '../../../contracts/Address';
import type { RootState } from '../../../store/types';
import { TabContent } from '../../../routes/RouterOutlet.styles';
import ErrorPage from '../../../shared/components/ErrorPage';
import LoadingPage from '../../../shared/components/LoadingPage';
import PageTitle from '../../../shared/components/PageTitle';
import PurchaseDisclaimer from '../../../shared/components/PurchaseDisclaimer';
import { formatPhoneNumber, toUsd, trackViewedPage } from '../../../utilities';
import { personalDetailsOperations, personalDetailsSelectors } from '../ducks';
import {
  ADD_PHONE_NUMBER,
  EDIT_DELIVERY_ADDRESS,
  EDIT_HOME_ADDRESS,
  EDIT_MAILING_ADDRESS,
  EDIT_PASSWORD_PATH,
  PERSONAL_INFORMATION_PATH
} from './constants';
import Section from './Section';
import { EditLink, Field, Label, MailingAddressCopy, Row, Value } from './Section/Section.styles';

type StateProps = {
  canEditPersonalDetails: boolean;
  dateOfBirth?: string;
  deliveryAddress: Address | null;
  emailAddress: string;
  fullName: string;
  hasSsn: boolean;
  homeAddress: Address | null;
  income: string;
  isDeliveryAddressSameAsHomeAddress?: boolean;
  isIn7DMBG: boolean;
  isLoadingPersonalDetails: boolean;
  isMailingAddressSameAsHomeAddress?: boolean;
  isOrderInProgress: boolean;
  isOrderPlaced: boolean;
  isRandomPassword?: boolean;
  mailingAddress: Address | null;
  personalDetailsError: Record<string, unknown>;
  phoneNumber: string;
  preferredLanguage: string | null;
  preferredName: string | null;
  shouldShowDeliveryAddress: boolean;
  shouldShowHomeAddress: boolean;
  shouldShowMailingAddress: boolean;
  shouldShowPersonalInfoSection: boolean;
  userId: string;
};

type DispatchProps = {
  fetchUserProfileData: (userId: string) => void;
};

function ShowPersonalDetails({
  userId,
  fullName,
  emailAddress,
  phoneNumber,
  shouldShowPersonalInfoSection,
  shouldShowHomeAddress,
  shouldShowMailingAddress,
  shouldShowDeliveryAddress,
  isMailingAddressSameAsHomeAddress,
  isDeliveryAddressSameAsHomeAddress,
  homeAddress,
  deliveryAddress,
  mailingAddress,
  income,
  hasSsn,
  dateOfBirth,
  canEditPersonalDetails,
  isLoadingPersonalDetails,
  personalDetailsError,
  isOrderInProgress,
  isOrderPlaced,
  isIn7DMBG,
  preferredName,
  preferredLanguage,
  isRandomPassword,
  fetchUserProfileData
}: StateProps & DispatchProps) {
  const history = useHistory();

  // eslint-disable-next-line radix
  const shouldShowIncome = Boolean(parseInt(income));
  const shouldShowPreferredName = !!preferredName?.trim();
  const hasError = !isEqual(personalDetailsError, {});
  const shouldShowError = !isLoadingPersonalDetails && hasError;
  const shouldShowComponent = !isLoadingPersonalDetails && !hasError;
  const hasEmptyMailingAddress = mailingAddress && mailingAddress.address.street === '';
  const hasEmptyPhoneNumber = !phoneNumber;

  const shouldShowEditIcon =
    canEditPersonalDetails && !isIn7DMBG && !isOrderPlaced && !isOrderInProgress;
  const showPurchaseDisclaimer =
    canEditPersonalDetails && (isIn7DMBG || isOrderPlaced || isOrderInProgress);

  const navigateToEditPreferredName = () => {
    history.push(PERSONAL_INFORMATION_PATH, {
      autoFocusInput: 'preferredName'
    });
    trackViewedPage(PERSONAL_INFORMATION_PATH);
  };

  const navigateToAddPhoneNumber = () => {
    history.push(ADD_PHONE_NUMBER, {
      autoFocusInput: 'phone-number'
    });
    trackViewedPage(ADD_PHONE_NUMBER);
  };

  /** Initialize user profile data */
  useEffect(() => {
    if (userId) {
      fetchUserProfileData(userId);
    }
  }, [userId]);

  return (
    <>
      <PageTitle title="Personal Details" />
      {isLoadingPersonalDetails && <LoadingPage />}
      {shouldShowError && <ErrorPage />}

      {shouldShowComponent && (
        <TabContent>
          {shouldShowPersonalInfoSection && (
            <Section
              title="Personal Information"
              editPageUrl={PERSONAL_INFORMATION_PATH}
              shouldShowEditIcon={shouldShowEditIcon}
            >
              {showPurchaseDisclaimer && (
                <PurchaseDisclaimer
                  isIn7DMBG={isIn7DMBG}
                  isOrderPlaced={isOrderPlaced}
                  isOrderInProgress={isOrderInProgress}
                />
              )}
              {!!fullName && (
                <Field>
                  <Label>Name</Label>
                  <Value>
                    {fullName}{' '}
                    {!shouldShowPreferredName && shouldShowEditIcon && (
                      <EditLink onClick={() => navigateToEditPreferredName()}>
                        {' '}
                        Go by a different name?{' '}
                      </EditLink>
                    )}
                  </Value>{' '}
                </Field>
              )}
              {shouldShowPreferredName && (
                <Field>
                  <Label>Preferred Name</Label>
                  <Value>{preferredName}</Value>
                </Field>
              )}
              {!!emailAddress && (
                <Field>
                  <Label>Email</Label>
                  <Value>{emailAddress}</Value>
                </Field>
              )}
              <Field>
                <Label>Phone</Label>
                <Value>
                  {formatPhoneNumber(phoneNumber)}
                  {hasEmptyPhoneNumber && (
                    <EditLink onClick={() => navigateToAddPhoneNumber()}>
                      {' '}
                      Add Phone Number
                    </EditLink>
                  )}
                </Value>
              </Field>
              {!!dateOfBirth && (
                <Field>
                  <Label>Date Of Birth</Label>
                  <Value>{dateOfBirth}</Value>
                </Field>
              )}
              {!!preferredLanguage && (
                <Field>
                  <Label>Preferred Language</Label>
                  <Value>{preferredLanguage}</Value>
                </Field>
              )}
              {shouldShowIncome && (
                <Field>
                  <Label>Income</Label>
                  <Value>{toUsd(income)}</Value>
                </Field>
              )}
              {hasSsn && (
                <Field>
                  <Label>Social Security Number</Label>
                  <Value>*********</Value>
                </Field>
              )}
            </Section>
          )}
          {!isRandomPassword && (
            <Section title="Security" editPageUrl={EDIT_PASSWORD_PATH}>
              <Field>
                <Label>Password</Label>
                <Value>•••••••••</Value>
              </Field>
            </Section>
          )}

          {shouldShowHomeAddress && (
            <Section
              title="Home Address"
              editPageUrl={EDIT_HOME_ADDRESS}
              shouldShowEditIcon={shouldShowEditIcon}
            >
              {!!homeAddress?.address?.street && (
                <Field>
                  <Label>Street Address</Label>
                  <Value>
                    {homeAddress.address.street}{' '}
                    {homeAddress.address.street2 && homeAddress.address.street2}
                  </Value>
                </Field>
              )}

              {!!homeAddress?.address?.city && (
                <Field>
                  <Label>City</Label>
                  <Value>{homeAddress.address.city}</Value>
                </Field>
              )}

              {!!homeAddress?.address?.state && (
                <Field>
                  <Label>State</Label>
                  <Value>{homeAddress.address.state}</Value>
                </Field>
              )}

              {!!homeAddress?.address?.zipCode?.zip5 && (
                <Field>
                  <Label>Zip</Label>
                  <Value>{homeAddress.address.zipCode.zip5}</Value>
                </Field>
              )}
            </Section>
          )}

          {shouldShowMailingAddress && (
            <Section
              title="Mailing Address"
              editPageUrl={EDIT_MAILING_ADDRESS}
              shouldShowEditIcon={shouldShowEditIcon}
            >
              {isMailingAddressSameAsHomeAddress || hasEmptyMailingAddress ? (
                <Row>
                  <MailingAddressCopy>
                    Mailing address is the same as home address
                  </MailingAddressCopy>
                </Row>
              ) : (
                <>
                  {!!mailingAddress?.address?.street && (
                    <Field>
                      <Label>Street Address</Label>
                      <Value>
                        {mailingAddress.address.street}{' '}
                        {mailingAddress.address.street2 && mailingAddress.address.street2}
                      </Value>
                    </Field>
                  )}

                  {!!mailingAddress?.address?.city && (
                    <Field>
                      <Label>City</Label>
                      <Value>{mailingAddress.address.city}</Value>
                    </Field>
                  )}

                  {!!mailingAddress?.address?.state && (
                    <Field>
                      <Label>State</Label>
                      <Value>{mailingAddress.address.state}</Value>
                    </Field>
                  )}

                  {!!mailingAddress?.address?.zipCode?.zip5 && (
                    <Field>
                      <Label>Zip</Label>
                      <Value>{mailingAddress.address.zipCode.zip5}</Value>
                    </Field>
                  )}
                </>
              )}
            </Section>
          )}

          {shouldShowDeliveryAddress && (
            <Section
              title="Delivery Address"
              editPageUrl={EDIT_DELIVERY_ADDRESS}
              shouldShowEditIcon={shouldShowEditIcon}
            >
              {isDeliveryAddressSameAsHomeAddress ? (
                <Row>
                  <MailingAddressCopy>
                    Delivery address is the same as home address
                  </MailingAddressCopy>
                </Row>
              ) : (
                <>
                  {!!deliveryAddress?.address?.street && (
                    <Field>
                      <Label>Street Address</Label>
                      <Value>
                        {deliveryAddress.address.street}{' '}
                        {deliveryAddress.address.street2 && deliveryAddress.address.street2}
                      </Value>
                    </Field>
                  )}

                  {!!deliveryAddress?.address?.city && (
                    <Field>
                      <Label>City</Label>
                      <Value>{deliveryAddress.address.city}</Value>
                    </Field>
                  )}

                  {!!deliveryAddress?.address?.state && (
                    <Field>
                      <Label>State</Label>
                      <Value>{deliveryAddress.address.state}</Value>
                    </Field>
                  )}

                  {!!deliveryAddress?.address?.zipCode?.zip5 && (
                    <Field>
                      <Label>Zip</Label>
                      <Value>{deliveryAddress.address.zipCode.zip5}</Value>
                    </Field>
                  )}
                </>
              )}
            </Section>
          )}
        </TabContent>
      )}
    </>
  );
}

const mapStateToProps = (state: RootState) => {
  const {
    getUserId,
    getFullName,
    getEmailAddress,
    getPhoneNumber,
    getShouldShowPersonalInfoSection,
    getShouldShowHomeAddress,
    getShouldShowMailingAddress,
    getShouldShowDeliveryAddress,
    getIsMailingAddressSameAsHomeAddress,
    getIsDeliveryAddressSameAsHomeAddress,
    getHomeAddress,
    getDeliveryAddress,
    getMailingAddress,
    getIncome,
    getShouldShowSsn,
    getDateOfBirth,
    getCanEditPersonalDetails,
    getIsLoadingPersonalDetails,
    getPersonalDetailsError,
    getIsOrderInProgress,
    getIsOrderPlaced,
    getIsIn7DMBG,
    getPreferredName,
    getPreferredLanguage,
    getIsRandomPassword
  } = personalDetailsSelectors;

  return {
    userId: getUserId(state),
    fullName: getFullName(state),
    emailAddress: getEmailAddress(state),
    phoneNumber: getPhoneNumber(state),
    shouldShowPersonalInfoSection: getShouldShowPersonalInfoSection(state),
    shouldShowHomeAddress: getShouldShowHomeAddress(state),
    shouldShowMailingAddress: getShouldShowMailingAddress(state),
    shouldShowDeliveryAddress: getShouldShowDeliveryAddress(state),
    isMailingAddressSameAsHomeAddress: getIsMailingAddressSameAsHomeAddress(state),
    isDeliveryAddressSameAsHomeAddress: getIsDeliveryAddressSameAsHomeAddress(state),
    homeAddress: getHomeAddress(state),
    deliveryAddress: getDeliveryAddress(state),
    mailingAddress: getMailingAddress(state),
    income: getIncome(state),
    hasSsn: getShouldShowSsn(state),
    dateOfBirth: getDateOfBirth(state),
    canEditPersonalDetails: getCanEditPersonalDetails(state),
    isLoadingPersonalDetails: getIsLoadingPersonalDetails(state),
    personalDetailsError: getPersonalDetailsError(state),
    isOrderInProgress: getIsOrderInProgress(state),
    isOrderPlaced: getIsOrderPlaced(state),
    isIn7DMBG: getIsIn7DMBG(state),
    preferredName: getPreferredName(state),
    preferredLanguage: getPreferredLanguage(state),
    isRandomPassword: getIsRandomPassword(state)
  };
};

const mapDispatchToProps = {
  fetchUserProfileData: personalDetailsOperations.fetchUserProfileData
};

export default connect(mapStateToProps, mapDispatchToProps)(ShowPersonalDetails);
